import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { usePASClientAuth } from '../../context/PASClientAuthContext';
import { CheckCircle, Loader2, ExternalLink, Fuel, XCircle, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-eth`;

export default function GasPurchaseSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, loading: authLoading, pasClient } = usePASClientAuth();
  const [status, setStatus] = useState('confirming'); // confirming, completed, processing, failed, no_auth
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const purchaseId = searchParams.get('purchase_id');

  useEffect(() => {
    if (authLoading) return;
    
    if (!pasClient || !token) {
      setStatus('no_auth');
      return;
    }
    
    if (!purchaseId) {
      setError('No purchase ID provided');
      setStatus('failed');
      return;
    }

    const confirm = async () => {
      try {
        const res = await axios.post(
          `${API}/gas/purchases/${purchaseId}/confirm`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setResult(res.data);
        
        if (res.data.status === 'completed') {
          setStatus('completed');
        } else if (res.data.status === 'failed') {
          setError(res.data.error || 'Transfer failed');
          setStatus('failed');
        } else {
          setStatus('processing');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || 'Failed to confirm purchase';
        setError(detail);
        // If payment not completed, show different message
        if (detail.includes('Payment not completed')) {
          setStatus('payment_pending');
        } else {
          setStatus('failed');
        }
      }
    };

    confirm();
  }, [token, authLoading, purchaseId, pasClient]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-orange-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 max-w-md w-full text-center">
        {status === 'confirming' && (
          <>
            <Loader2 className="w-16 h-16 text-orange-400 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Processing Payment</h1>
            <p className="text-slate-400">Sending ETH to your wallet...</p>
          </>
        )}

        {status === 'completed' && result && (
          <>
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Gas Sent!</h1>
            <p className="text-slate-400 mb-4">
              {result.eth_amount?.toFixed(6)} ETH has been sent to your wallet
            </p>
            
            {result.explorer_url && (
              <a
                href={result.explorer_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 mb-6"
              >
                <ExternalLink className="w-4 h-4" />
                View on Etherscan
              </a>
            )}

            <div className="bg-slate-900/60 rounded-lg p-4 mb-6 text-left">
              <div className="flex items-center gap-3">
                <Fuel className="w-5 h-5 text-orange-400" />
                <div>
                  <p className="text-sm text-slate-400">Transaction Hash</p>
                  <p className="text-xs text-slate-500 font-mono break-all">{result.tx_hash}</p>
                </div>
              </div>
            </div>
          </>
        )}

        {status === 'processing' && (
          <>
            <Loader2 className="w-16 h-16 text-amber-400 animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-bold text-white mb-2">Payment Processing</h1>
            <p className="text-slate-400 mb-4">
              Your payment is being processed. ETH will be sent shortly.
            </p>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Transfer Failed</h1>
            <p className="text-red-300 mb-4">{error}</p>
            <p className="text-slate-400 text-sm">
              If you completed payment, please contact support with your purchase ID: <span className="font-mono text-xs">{purchaseId}</span>
            </p>
          </>
        )}

        {status === 'payment_pending' && (
          <>
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Payment Pending</h1>
            <p className="text-slate-400 mb-4">
              Your payment has not been confirmed yet. If you completed payment, please wait a moment and refresh this page.
            </p>
          </>
        )}

        {status === 'no_auth' && (
          <>
            <div className="w-16 h-16 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="w-10 h-10 text-amber-400" />
            </div>
            <h1 className="text-xl font-bold text-white mb-2">Please Log In</h1>
            <p className="text-slate-400 mb-4">
              You need to be logged in to confirm your gas purchase.
            </p>
            <button
              onClick={() => navigate('/pas/login')}
              className="mb-4 px-6 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium"
            >
              Log In
            </button>
          </>
        )}

        <button
          onClick={() => navigate('/pas/banking')}
          className="mt-4 w-full py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-lg font-medium"
        >
          Back to Banking
        </button>
      </div>
    </div>
  );
}
